import { type CSSProperties } from "react";
import { formatMoney, formatMoneyCode } from "../../lib/format";

/**
 * Vrednost pokazatelja sa podelom po valutama ispod nje.
 *
 * Zbir preko valuta bez preračuna nema značenje, pa veliki broj ostaje u evrima. Ali sam evro
 * iznos vara: od 752 otvorena računa 507 je u dinarima, pa „5,5 miliona evra" navodi na zaključak
 * da je posao devizni. Zato podela stoji odmah ispod broja, vidljiva bez klika i bez prelaska
 * mišem, u iznosima kakvi stoje na računima.
 *
 * Kad je ekran sveden na jednu valutu, veliki broj je već tačan iznos u njoj i podele nema.
 *
 * The same component carries the balance on the card of one company: there the split comes from
 * its accounts, the amounts keep their cents, and a note under the split names the day of the rate
 * the euro figure stands on.
 */
export function IznosSaValutama<T extends { currency: string }>({
  eur,
  byCurrency,
  uzmi,
  locale,
  currency,
  napomena,
  tacno = false,
}: {
  /** Zbir u evrima, ili tačan iznos kad je `currency` izabrana valuta. */
  eur: number;
  byCurrency: T[];
  /** Koji deo podele ovaj pokazatelj prikazuje: saldo, dospelo ili nedospelo. */
  uzmi: (v: T) => number;
  locale: string;
  currency?: string;
  /** A small line under the split, such as the day of the rate; shown only with the split. */
  napomena?: string;
  /** The split with cents: the debt of one company, rather than rounded millions on an overview. */
  tacno?: boolean;
}) {
  // An amount below zero (an advance) is part of the sum as much as a debt is, so it is listed too.
  const stavke = currency
    ? []
    : byCurrency.map((v) => ({ v: v.currency, n: uzmi(v) })).filter((x) => x.n !== 0);

  return (
    <span style={{ display: "block" }}>
      {/* Manje od podrazumevanih 28px iz `.kpi .val`: „5.545.425,84 €“ je trinaest znakova i na
          28px se ne uklapa u karticu, pa ga je okvir sekao. */}
      <span style={iznos}>{formatMoney(eur, currency ?? "EUR", locale)}</span>
      {/* Podela se ne crta kad ne kaže ništa novo: jedna valuta koja je evro već stoji gore.
          Jedna valuta koja NIJE evro se crta — tu je razlika između evra i dinara najveća. */}
      {(stavke.length > 1 || (stavke.length === 1 && stavke[0].v !== "EUR")) && (
        <span style={podela}>
          {stavke.map((x) => (
            <span key={x.v} style={{ fontVariantNumeric: "tabular-nums" }}>
              {formatMoneyCode(x.n, x.v, locale, tacno ? undefined : { maximumFractionDigits: 0 })}
            </span>
          ))}
          {napomena && <span style={napomenaStil}>{napomena}</span>}
        </span>
      )}
    </span>
  );
}

/** Ista mera i za pokazatelje bez podele, da kartice u redu stoje ujednačeno. */
export const iznos: CSSProperties = {
  display: "block", fontSize: 23, lineHeight: 1.2,
  fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
};
/**
 * Podela stoji kao stubac, ne kao red koji se prelama.
 *
 * Sa tri valute u uskoj kartici prelamanje je ionako davalo tri reda, samo slučajno; namerno
 * poređani, iznosi se čitaju jedan ispod drugog i cifre im se poklapaju.
 */
const podela: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 1,
  fontSize: 12.5, fontWeight: 400, color: "var(--ink-3)", lineHeight: 1.45,
  marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)",
};
const napomenaStil: CSSProperties = { fontSize: 11, marginTop: 2 };
